import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { KeyRound, Plus, Copy, Ban, Trash2, RefreshCw, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch (e) {
    return iso;
  }
};

const ApiKeysPage = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [semAcesso, setSemAcesso] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [novaChave, setNovaChave] = useState(null); // { key, name } — exibida uma única vez

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/api-keys`, { headers: headers() });
      setKeys(response.data);
      setSemAcesso(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setSemAcesso(true);
      } else {
        toast.error('Erro ao carregar as chaves de API');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const createKey = async () => {
    if (!name.trim()) {
      toast.error('Informe um nome para a chave');
      return;
    }
    try {
      setCreating(true);
      const response = await axios.post(`${API}/api/api-keys`, { name: name.trim() }, { headers: headers() });
      setShowCreate(false);
      setName('');
      setNovaChave({ key: response.data.key, name: response.data.name });
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar a chave');
    } finally {
      setCreating(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(novaChave.key);
      toast.success('Chave copiada');
    } catch (e) {
      toast.error('Não foi possível copiar. Selecione e copie manualmente.');
    }
  };

  const revokeKey = async (key) => {
    if (!window.confirm(`Revogar a chave "${key.name}"? As integrações que a usam deixarão de funcionar.`)) return;
    try {
      await axios.delete(`${API}/api/api-keys/${key.id}`, { headers: headers() });
      toast.success('Chave revogada');
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao revogar a chave');
    }
  };

  const deleteRecord = async (key) => {
    if (!window.confirm(`Excluir o registro da chave "${key.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/api-keys/${key.id}?excluir=true`, { headers: headers() });
      toast.success('Registro excluído');
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir o registro');
    }
  };

  const docsUrl = `${API || ''}/api/v1/docs`;

  if (semAcesso) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> API Pública
            </CardTitle>
            <CardDescription>
              Você não tem acesso à API pública. Peça a um administrador para habilitar a permissão
              &quot;API Pública (integrações)&quot; no seu perfil (ou confirme que a API está liberada para esta conta).
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" /> API Pública — Chaves de acesso
              </CardTitle>
              <CardDescription>
                Use a chave no cabeçalho <code>Authorization: Bearer gpk_live_…</code> das chamadas a{' '}
                <code>/api/v1</code>. A chave só é exibida uma vez, no momento da criação.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.open(docsUrl, '_blank', 'noopener')}>
                <ExternalLink className="h-4 w-4 mr-2" /> Documentação (Swagger)
              </Button>
              <Button variant="outline" onClick={loadKeys} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
              </Button>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" /> Nova chave
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Chave</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead>Último uso</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {loading ? 'Carregando…' : 'Nenhuma chave criada ainda.'}
                  </TableCell>
                </TableRow>
              )}
              {keys.map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell>
                    <code>{k.prefix}…</code>
                  </TableCell>
                  <TableCell>{formatDate(k.created_at)}</TableCell>
                  <TableCell>{formatDate(k.last_used_at)}</TableCell>
                  <TableCell>
                    {k.active ? <Badge>Ativa</Badge> : <Badge variant="secondary">Revogada</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {k.active ? (
                      <Button size="sm" variant="outline" onClick={() => revokeKey(k)}>
                        <Ban className="h-4 w-4 mr-1" /> Revogar
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => deleteRecord(k)}>
                        <Trash2 className="h-4 w-4 mr-1" /> Excluir registro
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Criar chave */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova chave de API</DialogTitle>
            <DialogDescription>
              A chave herda as permissões do seu perfil. Se elas mudarem, a chave acompanha na hora.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="api-key-name">Nome da chave</Label>
            <Input
              id="api-key-name"
              placeholder="Ex.: Integração ERP"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createKey()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
            <Button onClick={createKey} disabled={creating}>
              {creating ? 'Criando…' : 'Criar chave'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exibir a chave uma única vez */}
      <Dialog open={!!novaChave} onOpenChange={(open) => !open && setNovaChave(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Chave criada: {novaChave?.name}</DialogTitle>
            <DialogDescription>
              Copie e guarde agora: <strong>esta chave não será exibida novamente</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={novaChave?.key || ''} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
            <Button variant="outline" onClick={copyKey}>
              <Copy className="h-4 w-4 mr-1" /> Copiar
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setNovaChave(null)}>Já guardei a chave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeysPage;
